(() => {
  const element = document.getElementById('HerblWelcomeGiftsConfig');
  if (!element) return;
  const config = JSON.parse(element.textContent);
  if (!config.enabled || !config.productId || !Object.values(config.gifts).every(Boolean)) return;

  class WelcomeGifts {
    constructor(settings) {
      this.config = settings;
      this.queue = Promise.resolve();
      this.giftIds = new Set(Object.values(settings.gifts).filter(Boolean).map(String));
      this.root = window.Shopify?.routes?.root || '/';
    }

    enqueue(operation) {
      const next = this.queue.then(operation);
      this.queue = next.catch(() => {});
      return next;
    }

    desired(items) {
      let hasThirty = false;
      let hasNinety = false;
      for (const item of items) {
        if (String(item.product_id) !== String(this.config.productId)) continue;
        const plan = String(item.selling_plan_allocation?.selling_plan?.id || '');
        hasThirty ||= plan === String(this.config.plan30);
        hasNinety ||= plan === String(this.config.plan90);
      }
      const ids = [];
      if (hasThirty || hasNinety) ids.push(this.config.gifts.card);
      if (hasNinety) ids.push(this.config.gifts.plan, this.config.gifts.tracker);
      return new Set(ids.filter(Boolean).map(String));
    }

    changes(cart) {
      const wanted = this.desired(cart.items);
      const found = new Set();
      const updates = {};
      for (const item of cart.items) {
        const id = String(item.variant_id || item.id);
        if (!this.giftIds.has(id)) continue;
        const retain = wanted.has(id) && !found.has(id) && !item.selling_plan_allocation;
        if (retain) found.add(id);
        const quantity = retain ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      }
      const additions = [...wanted].filter(id => !found.has(id)).map(id => ({
        id: Number(id), quantity: 1, properties: { _herbl_welcome_gift: 'v1' }
      }));
      return { updates, additions };
    }

    async json(url, options) {
      const response = await fetch(url, options);
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || this.config.error);
      }
      return data;
    }

    post(path, body) {
      return this.json(this.root + path, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
    }

    async reconcile(cart, sections) {
      const { updates, additions } = this.changes(cart);
      const rendering = { sections, sections_url: window.location.pathname };
      let changed = false;
      if (Object.keys(updates).length) {
        cart = await this.post('cart/update.js', { updates, ...rendering });
        changed = true;
      }
      if (additions.length) {
        await this.post('cart/add.js', { items: additions });
        cart = await this.post('cart/update.js', { updates: {}, ...rendering });
        changed = true;
      }
      return { cart, changed };
    }

    mutate(url, options) {
      return this.enqueue(async () => {
        let groupItem;
        if (!(options.body instanceof FormData)) {
          const request = JSON.parse(options.body);
          if (request.herbl_grouped) {
            const identity = request.herbl_group;
            delete request.herbl_grouped;
            delete request.herbl_group;
            const before = await this.json(this.root + 'cart.js');
            // Resolve against stable product/plan/properties, since discounts can change keys and line positions.
            groupItem = identity ? {
              variant_id: identity.variantId,
              selling_plan_allocation: { selling_plan: { id: identity.planId } },
              properties: identity.properties
            } : request.id ? before.items.find(item => item.key === request.id) : before.items[request.line - 1];
            const matches = before.items.filter(item => this.sameGroup(item, groupItem));
            const rendering = { sections: request.sections, sections_url: request.sections_url };
            if (matches.length === 1 && request.quantity > 0) {
              // Preserve quantity-property repairs when Shopify has not split the line.
              options = { ...options, body: JSON.stringify({ id: matches[0].key, quantity: request.quantity, ...(request.properties ? { properties: request.properties } : {}), ...rendering }) };
              if (request.properties) groupItem.properties = request.properties;
            } else {
              // Remove every split line together; an already removed group is a safe no-op.
              url = this.root + 'cart/update.js';
              const updates = Object.fromEntries(matches.map((item,index) => [item.key,index === 0 ? request.quantity : 0]));
              options = { ...options, body: JSON.stringify({ updates, ...rendering }) };
            }
          }
        }
        const response = await fetch(url, options);
        let state = await response.json();
        if (!response.ok || state.status || state.errors) return state;
        const body = options.body instanceof FormData ? Object.fromEntries(options.body) : JSON.parse(options.body);
        const sections = (Array.isArray(body.sections) ? body.sections : String(body.sections || '').split(',')).filter(Boolean);
        const cart = state.token ? state : await this.json(this.root + 'cart.js');
        let result;
        try {
          result = await this.reconcile(cart, sections);
        } catch (error) {
          this.showError(error);
          throw new Error(this.config.error);
        }
        if (result.changed) state = result.cart;
        if (groupItem) state.herbl_group_quantity = result.cart.items.filter(item => this.sameGroup(item, groupItem)).reduce((total,item) => total + item.quantity,0);
        return state;
      });
    }

    sameGroup(item, target) {
      const normalized = properties => {
        const entries = Array.isArray(properties) ? properties : Object.entries(properties || {});
        return JSON.stringify(entries.map(([key,value]) => [String(key),String(value)]).sort(([a],[b]) => a.localeCompare(b)));
      };
      return target && String(item.variant_id) === String(target.variant_id)
        && String(item.selling_plan_allocation?.selling_plan?.id || '') === String(target.selling_plan_allocation?.selling_plan?.id || '')
        && normalized(item.properties) === normalized(target.properties);
    }

    sectionIds() {
      const ids = ['cart-drawer', 'cart-icon-bubble'];
      for (const id of ['main-cart-items', 'main-cart-footer']) {
        const section = document.getElementById(id);
        if (section?.dataset.id) ids.push(section.dataset.id);
      }
      return ids;
    }

    render(cart) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && cart.sections?.['cart-drawer']) {
        drawer.renderContents(cart, false);
        drawer.classList.toggle('is-empty', cart.item_count === 0);
      } else if (cart.sections?.['cart-icon-bubble']) {
        const html = new DOMParser().parseFromString(cart.sections['cart-icon-bubble'], 'text/html');
        const target = document.getElementById('cart-icon-bubble');
        if (target) target.innerHTML = html.querySelector('.shopify-section').innerHTML;
      }
      for (const id of ['main-cart-items', 'main-cart-footer']) {
        const section = document.getElementById(id);
        const markup = cart.sections?.[section?.dataset.id];
        if (!section || !markup) continue;
        const html = new DOMParser().parseFromString(markup, 'text/html');
        const target = section.querySelector('.js-contents');
        const source = html.querySelector('.js-contents');
        if (target && source) target.innerHTML = source.innerHTML;
        section.classList.toggle('is-empty', cart.item_count === 0);
      }
    }

    sync() {
      return this.enqueue(async () => {
        const cart = await this.json(this.root + 'cart.js');
        const result = await this.reconcile(cart, this.sectionIds());
        if (result.changed) this.render(result.cart);
        return result.cart;
      });
    }

    showError(error) {
      const target = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
      if (target) target.textContent = this.config.error;
      console.error('Welcome gift update failed', error);
    }
  }

  const gifts = new WelcomeGifts(config);
  window.HerblWelcomeGifts = gifts;
  const initialize = () => {
    gifts.sync().catch(error => gifts.showError(error));
    document.addEventListener('submit', async event => {
      const button = event.submitter;
      if (!button || button.name !== 'checkout' || button.dataset.giftChecked === 'true') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      button.disabled = true;
      try {
        await gifts.sync();
        const form = document.getElementById(event.target.id) || event.target;
        const currentButton = form.querySelector('[name="checkout"]') || document.querySelector('[name="checkout"][form="' + form.id + '"]') || button;
        currentButton.dataset.giftChecked = 'true';
        currentButton.disabled = false;
        form.requestSubmit(currentButton);
        delete currentButton.dataset.giftChecked;
      } catch (error) {
        gifts.showError(error);
      } finally {
        button.disabled = false;
      }
    }, true);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
